import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/config";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bell, Mail, Sparkles, Megaphone, MessageSquare, Save } from "lucide-react";

const ROWS = [
  {
    key: "daily_reminder",
    icon: Bell,
    title: "Daily protocol reminder",
    sub: "A gentle nudge in the evening if you haven't checked in yet — plus a stronger 'we miss you' after 3 days.",
  },
  {
    key: "weekly_checkin",
    icon: Mail,
    title: "Weekly check-in email",
    sub: "Your progress summary every Sunday — streak, completion rate, and Dr. Slava's protocol of the week.",
  },
  {
    key: "announcements",
    icon: Sparkles,
    title: "Product announcements",
    sub: "New workshops, cohort openings, and platform updates. Rare — a couple per month.",
  },
];

const MARKETING_ROWS = [
  {
    key: "consent_email_marketing",
    icon: Megaphone,
    title: "Marketing emails",
    sub: "Newsletter drops, protocol releases, and offers. Every message includes a one-click unsubscribe.",
  },
  {
    key: "consent_sms_marketing",
    icon: MessageSquare,
    title: "SMS updates & offers",
    sub: "Text reminders and protocol drops. Reply STOP anytime. Msg & data rates may apply.",
  },
];

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [phone, setPhone] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/notifications");
        setPrefs(data);
        setPhone(data.phone || "");
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load preferences.");
      }
    })();
  }, []);

  const toggle = async (key, next) => {
    const prev = prefs;
    setPrefs({ ...prefs, [key]: next });
    setSaving(key);
    const isMarketing = key === "consent_email_marketing" || key === "consent_sms_marketing";
    try {
      const url = isMarketing ? "/me/marketing-consent" : "/me/notifications";
      const { data } = await api.patch(url, { [key]: next });
      setPrefs(data);
      toast.success(next ? "Turned on" : "Turned off");
    } catch (err) {
      setPrefs(prev);
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  const savePhone = async () => {
    setSavingPhone(true);
    try {
      const { data } = await api.patch("/me/marketing-consent", { phone });
      setPrefs(data);
      toast.success("Mobile number updated");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSavingPhone(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl space-y-10">
        <div className="fade-up">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">Settings</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Notifications.</h1>
          <p className="text-white/55 mt-2 max-w-lg">
            Turn any of these off any time. Your account and billing emails always come through.
          </p>
        </div>

        <div className="space-y-3" data-testid="notification-prefs">
          {ROWS.map(({ key, icon: Icon, title, sub }, i) => (
            <div
              key={key}
              className={`glass rounded-2xl p-5 flex items-center justify-between gap-6 fade-up delay-${i + 1}`}
              data-testid={`pref-row-${key}`}
            >
              <div className="flex items-start gap-4 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-[#FF8A00] shrink-0">
                  <Icon size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-white/90 font-medium">{title}</div>
                  <div className="text-xs text-white/50 mt-1 leading-relaxed">{sub}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {saving === key && <span className="text-[11px] text-white/40">saving…</span>}
                <Switch
                  checked={!!prefs?.[key]}
                  onCheckedChange={(v) => toggle(key, v)}
                  disabled={!prefs || saving === key}
                  data-testid={`toggle-${key}`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Marketing consent — separate section so it's clearly opt-in */}
        <div className="space-y-3">
          <div className="text-[11px] tracking-[0.22em] uppercase text-white/40">Marketing preferences</div>
          {MARKETING_ROWS.map(({ key, icon: Icon, title, sub }) => (
            <div key={key}
              className="glass rounded-2xl p-5 flex items-center justify-between gap-6"
              data-testid={`pref-row-${key}`}>
              <div className="flex items-start gap-4 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-[#FF8A00] shrink-0">
                  <Icon size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-white/90 font-medium">{title}</div>
                  <div className="text-xs text-white/50 mt-1 leading-relaxed">{sub}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {saving === key && <span className="text-[11px] text-white/40">saving…</span>}
                <Switch
                  checked={!!prefs?.[key]}
                  onCheckedChange={(v) => toggle(key, v)}
                  disabled={!prefs || saving === key}
                  data-testid={`toggle-${key}`}
                />
              </div>
            </div>
          ))}

          {/* Phone number for SMS */}
          <div className="glass rounded-2xl p-5">
            <Label className="text-xs text-white/60">Mobile number (for SMS opt-in)</Label>
            <div className="flex gap-2 mt-2">
              <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                data-testid="marketing-phone-input"
                className="bg-[#0F0F0F] border-white/10" />
              <Button onClick={savePhone} disabled={savingPhone}
                data-testid="marketing-phone-save"
                className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                <Save size={13} className="mr-1.5"/> {savingPhone ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
